import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface CartItem {
  menu_item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  special_instructions?: string;
  vendor_id: string;
  vendor_name: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (menu_item_id: string) => void;
  updateQuantity: (menu_item_id: string, quantity: number) => void;
  updateInstructions: (menu_item_id: string, instructions: string) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
}

const CartContext = createContext<CartContextType>({
  cart: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  updateInstructions: () => {},
  clearCart: () => {},
  getCartTotal: () => 0,
  getCartCount: () => 0,
});

export const useCart = () => useContext(CartContext);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profileId } = useAuth();
  const [cart, setCart] = useState<CartItem[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    if (profileId) {
      const saved = localStorage.getItem(`cart_${profileId}`);
      if (saved) {
        try {
          setCart(JSON.parse(saved));
        } catch (err) {
          console.error('Failed to load cart:', err);
        }
      }
    }
  }, [profileId]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (profileId) {
      localStorage.setItem(`cart_${profileId}`, JSON.stringify(cart));
    }
  }, [cart, profileId]);

  const addToCart = (item: CartItem) => {
    setCart(prev => {
      // Check if item already exists
      const existing = prev.find(i => i.menu_item_id === item.menu_item_id);
      if (existing) {
        // Update quantity
        return prev.map(i =>
          i.menu_item_id === item.menu_item_id
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        );
      }
      // Add new item
      return [...prev, item];
    });
  };

  const removeFromCart = (menu_item_id: string) => {
    setCart(prev => prev.filter(i => i.menu_item_id !== menu_item_id));
  };

  const updateQuantity = (menu_item_id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(menu_item_id);
      return;
    }
    setCart(prev =>
      prev.map(i =>
        i.menu_item_id === menu_item_id ? { ...i, quantity } : i
      )
    );
  };

  const updateInstructions = (menu_item_id: string, instructions: string) => {
    setCart(prev =>
      prev.map(i =>
        i.menu_item_id === menu_item_id ? { ...i, special_instructions: instructions } : i
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateInstructions,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
